import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { getStoredSessionToken } from '../../services/sessionTokens';
import useAnalyticsWS, { isExpressPayoutRealtimeReason } from '../../hooks/useAnalyticsWS';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import Modal from '../../components/ui/Modal';
import TenantLogo from '../../components/admin/TenantLogo';
import PaymentBrand from '../../components/admin/PaymentBrand';
import {
  Activity,
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  Clock,
  Eye,
  RefreshCw,
  Search,
  ShieldAlert,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';

const DisputesPage = lazy(() => import('./DisputesPage'));

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' },
  { id: 'custom_month', label: 'Custom month' },
];

const STATUSES = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'paid', label: 'Paid' },
];

const RISK_INDEXES = [
  { id: 'all', label: 'All risk' },
  { id: 'low', label: 'Low' },
  { id: 'mid', label: 'Mid' },
  { id: 'high', label: 'High' },
];

const CASHOUT_MODES = [
  { id: 'all', label: 'All cashouts' },
  { id: 'normal', label: 'Normal' },
  { id: 'express', label: 'Express' },
  { id: 'priority', label: 'Priority' },
];

const NEPAL_TZ = 'Asia/Kathmandu';
const MONTH_NAMES = Array.from({ length: 12 }, (_, month) => new Intl.DateTimeFormat('en', {
  month: 'long', timeZone: 'UTC',
}).format(new Date(Date.UTC(2000, month, 1))));

const nepalYearMonth = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: NEPAL_TZ, year: 'numeric', month: '2-digit',
  }).formatToParts(now);
  return `${parts.find(part => part.type === 'year').value}-${parts.find(part => part.type === 'month').value}`;
};

export const defaultWithdrawalMonth = (now = new Date()) => {
  const [year, month] = nepalYearMonth(now).split('-').map(Number);
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
};

export const withdrawalMonthWindow = (value) => {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value || '')) return null;
  const [year, month] = value.split('-').map(Number);
  if (year < 1970) return null;
  // Nepal midnight is UTC minus 05:45. The existing API uses inclusive ends,
  // so do not include the following month's midnight in two monthly reports.
  const offset = (5 * 60 + 45) * 60 * 1000;
  return {
    start: new Date(Date.UTC(year, month - 1, 1) - offset).toISOString(),
    end: new Date(Date.UTC(year, month, 1) - offset - 1000).toISOString().replace('.000Z', '.999999Z'),
  };
};

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const int = (value) => Number(value || 0).toLocaleString();

const signedMoney = (value) => {
  const number = Number(value || 0);
  const prefix = number < 0 ? '-' : '';
  return `${prefix}${money(Math.abs(number))}`;
};

const time = (value) => {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: NEPAL_TZ,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '-';
  }
};

const sortByTimeDesc = (rows) => [...(rows || [])].sort((a, b) => {
  const left = new Date(a?.created_at || 0).getTime() || 0;
  const right = new Date(b?.created_at || 0).getTime() || 0;
  return right - left;
});

const statusClass = (group) => {
  if (group === 'pending') return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
  if (group === 'approved') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
  if (group === 'paid') return 'bg-green-500/15 text-green-300 border-green-500/30';
  if (group === 'failed' || group === 'rejected') return 'bg-red-500/15 text-red-300 border-red-500/30';
  return 'bg-white/10 text-gray-300 border-white/10';
};

export const withdrawalStatusLabel = (row) => {
  const providerStatus = String(row?.express_payout?.status || '').trim();
  const providerFinal = row?.express_payout?.final === true;
  if (row?.cashout_speed === 'express' && providerFinal && providerStatus === 'Done') {
    return 'Done';
  }
  return row?.status_group || row?.status || 'unknown';
};

export const canProceedExpressFromPortal = (basePath) => (
  String(basePath || '').includes('/api/admin/')
  || String(basePath || '').includes('/api/finance/')
);

const riskClass = (severity) => {
  if (severity === 'high') return 'border-red-500/40 bg-red-500/15 text-red-200';
  if (severity === 'medium') return 'border-yellow-500/40 bg-yellow-500/15 text-yellow-200';
  if (severity === 'low') return 'border-orange-500/35 bg-orange-500/10 text-orange-200';
  return 'border-white/10 bg-white/[0.03] text-gray-400';
};

export default function WithdrawalPortalPage({
  client = api,
  basePath = '/api/admin/withdrawal-portal',
  readOnly: readOnlyProp = false,
  canViewDisputes = false,
}) {
  const [view, setView] = useState('dashboard');
  const disputesAllowed = canViewDisputes && ['/api/admin/withdrawal-portal', '/api/tenant/withdrawal-portal'].includes(basePath);
  const showingDisputes = view === 'disputes' && disputesAllowed;
  useEffect(() => {
    if (view === 'disputes' && !disputesAllowed) setView('dashboard');
  }, [view, disputesAllowed]);
  const [period, setPeriod] = useState('today');
  const [customMonth, setCustomMonth] = useState(defaultWithdrawalMonth);
  const [status, setStatus] = useState('all');
  const [tenantId, setTenantId] = useState('all');
  const [cashoutMode, setCashoutMode] = useState('all');
  const [riskIndex, setRiskIndex] = useState('all');
  const [search, setSearch] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestPage, setRequestPage] = useState(null);
  const [requestResult, setRequestResult] = useState(null);
  const [requestError, setRequestError] = useState('');
  const requestSequence = useRef(0);
  const requestInFlight = useRef(null);
  const dashboardSequence = useRef(0);
  const dashboardInFlight = useRef(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [expressTenant, setExpressTenant] = useState(null);
  const [expressConfig, setExpressConfig] = useState(null);
  const [expressDryRunResult, setExpressDryRunResult] = useState(null);
  const [expressBusy, setExpressBusy] = useState(false);
  const [error, setError] = useState('');
  const readOnly = readOnlyProp || basePath === '/api/tenant/withdrawal-portal'
    || dashboard?.read_only || requestResult?.read_only || detail?.read_only;

  const periodParams = useMemo(() => (
    period === 'custom_month'
      ? { period: 'month', ...withdrawalMonthWindow(customMonth) }
      : { period }
  ), [period, customMonth]);
  const currentYear = Number(nepalYearMonth().split('-')[0]);
  const monthYears = Array.from({ length: currentYear - 1969 }, (_, index) => currentYear - index);
  const [selectedYear, selectedMonth] = customMonth.split('-');

  const requestFilters = useMemo(() => {
    const params = { ...periodParams, status, limit: 300 };
    if (tenantId !== 'all') params.tenant_id = tenantId;
    if (cashoutMode !== 'all') params.cashout_mode = cashoutMode;
    if (riskIndex !== 'all') params.risk_index = riskIndex;
    if (search.trim()) params.search = search.trim();
    if (view === 'risky') params.risk_only = true;
    return params;
  }, [periodParams, status, tenantId, cashoutMode, riskIndex, search, view]);

  const filterKey = JSON.stringify(requestFilters);
  const offset = requestPage?.filterKey === filterKey ? requestPage.offset : 0;
  const requestParams = useMemo(() => ({
    ...requestFilters,
    offset,
    ...(offset && requestPage?.window ? {
      start: requestPage.window.start, end: requestPage.window.end,
    } : {}),
  }), [requestFilters, offset, requestPage]);

  const loadDashboard = async (silent = false) => {
    const queryKey = JSON.stringify(periodParams);
    if (silent && dashboardInFlight.current?.queryKey === queryKey) return;
    const sequence = ++dashboardSequence.current;
    dashboardInFlight.current = { sequence, queryKey };
    if (!silent) {
      setLoading(true);
      setDashboard(null);
    }
    try {
      const res = await client.get(`${basePath}/dashboard`, { params: periodParams });
      if (sequence !== dashboardSequence.current) return;
      setDashboard(res.data);
      setTenants(res.data?.tenants || []);
      setError('');
    } catch (err) {
      if (sequence !== dashboardSequence.current) return;
      setError(err?.response?.data?.detail || 'Failed to load withdrawal portal dashboard');
    } finally {
      if (sequence === dashboardSequence.current) setLoading(false);
      if (dashboardInFlight.current?.sequence === sequence) dashboardInFlight.current = null;
    }
  };

  const loadRequests = async (silent = false) => {
    const queryKey = JSON.stringify(requestParams);
    // A slow monthly peer page must finish; background refreshes must not
    // repeatedly supersede it and leave the ledger loading forever.
    if (silent && requestInFlight.current?.queryKey === queryKey) return;
    const sequence = ++requestSequence.current;
    requestInFlight.current = { sequence, queryKey };
    if (!silent) {
      setRequestsLoading(true);
      setRequests([]);
      setRequestResult(null);
    }
    try {
      const res = await client.get(`${basePath}/requests`, { params: requestParams });
      if (sequence !== requestSequence.current) return;
      if (res.data?.pagination_version !== 1) {
        throw new Error('Withdrawal ledger upgrade in progress. Please refresh shortly.');
      }
      if (offset > 0 && offset >= Number(res.data.total)) {
        setRequestPage(null);
        return;
      }
      setRequests(res.data?.items || []);
      setRequestResult(res.data);
      if ((res.data?.tenants || []).length) setTenants(res.data.tenants);
      setRequestError('');
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setRequestError(err?.response?.data?.detail || err.message || 'Failed to load withdrawal requests. Try Refresh.');
    } finally {
      if (sequence === requestSequence.current) setRequestsLoading(false);
      if (requestInFlight.current?.sequence === sequence) requestInFlight.current = null;
    }
  };

  const realtimeToken = String(basePath || '').includes('/api/finance/')
    ? (typeof window !== 'undefined' ? window.localStorage.getItem('finance_token') : null)
    : getStoredSessionToken({ portal: 'admin' });

  useAnalyticsWS({
    token: realtimeToken,
    enabled: Boolean(realtimeToken) && !showingDisputes,
    onEvent: (message) => {
      if (showingDisputes) return;
      if (!isExpressPayoutRealtimeReason(message?.reason)) return;
      Promise.all([loadDashboard(true), loadRequests(true)]).catch(() => {});
    },
  });

  useAutoRefresh(
    () => Promise.all([loadDashboard(true), loadRequests(true)]),
    15000,
    !showingDisputes,
  );

  useEffect(() => {
    loadDashboard();
    return () => { dashboardSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodParams]);

  useEffect(() => {
    if (requestPage && requestPage.filterKey !== filterKey) setRequestPage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    loadRequests();
    return () => { requestSequence.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParams]);

  const openDetail = async (row) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await client.get(`${basePath}/request-detail`, {
        params: { request_id: row.id, kind: row.kind, source_vps: row.source_vps },
      });
      setDetail(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load withdrawal details');
    } finally {
      setDetailLoading(false);
    }
  };

  const refresh = () => {
    loadDashboard();
    if (offset) setRequestPage(null);
    else loadRequests();
  };

  const applyDetailAction = async (action, payload = {}) => {
    if (readOnly) return;
    const request = detail?.request;
    if (!request) return;
    setActionBusy(true);
    try {
      await client.post(`${basePath}/request-action`, {
        request_id: request.id,
        kind: request.kind,
        source_vps: request.source_vps,
        action,
        ...payload,
      });
      await Promise.all([loadDashboard(), loadRequests()]);
      const res = await client.get(`${basePath}/request-detail`, {
        params: { request_id: request.id, kind: request.kind, source_vps: request.source_vps },
      });
      setDetail(res.data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Withdrawal action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const openExpressConfig = async (tenant) => {
    if (readOnly) return;
    setExpressTenant(tenant);
    setExpressConfig(null);
    setExpressDryRunResult(null);
    setExpressBusy(true);
    try {
      const res = await client.get(`${basePath}/express-config`, {
        params: { tenant_id: tenant.tenant_id, source_vps: tenant.source_vps },
      });
      setExpressConfig(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load Express payout configuration');
      setExpressTenant(null);
    } finally {
      setExpressBusy(false);
    }
  };

  const saveExpressConfig = async (next) => {
    if (readOnly || !expressTenant) return;
    setExpressBusy(true);
    try {
      const res = await client.put(`${basePath}/express-config`, {
        ...next,
        tenant_id: expressTenant.tenant_id,
        source_vps: expressTenant.source_vps,
      });
      setExpressConfig(res.data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save Express payout configuration');
    } finally {
      setExpressBusy(false);
    }
  };

  const runExpressDryRun = async (payload = {}) => {
    if (readOnly || !expressTenant) return;
    setExpressBusy(true);
    try {
      const res = await client.post(`${basePath}/express-dry-run`, {
        tenant_id: expressTenant.tenant_id,
        source_vps: expressTenant.source_vps,
        ...payload,
      });
      setExpressDryRunResult((prev) => ({ ...(prev || {}), ...res.data }));
      setError('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Express dry-run failed');
    } finally {
      setExpressBusy(false);
    }
  };

  const totals = dashboard?.totals || {};
  const tenantRows = dashboard?.tenants || [];

  return (
    <div className="space-y-5" data-testid="withdrawal-portal-page">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-white">
            <Wallet className="h-6 w-6 text-[#D4AF37]" />
            Withdrawal Portal
          </h2>
          {!showingDisputes && <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            {period === 'custom_month' && <span>{MONTH_NAMES[Number(selectedMonth) - 1]} {selectedYear}</span>}
            {dashboard?.window?.start && <span>Nepal period: {time(dashboard.window.start)} - {time(dashboard.window.end)}</span>}
          </p>}
        </div>
        {!showingDisputes && <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="flex max-w-full overflow-x-auto rounded-xl border border-white/10 bg-[#0B0E14] p-1 overscroll-x-contain [-webkit-overflow-scrolling:touch]" data-testid="withdrawal-portal-periods">
            {PERIODS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setPeriod(item.id)}
                aria-pressed={period === item.id}
                className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  period === item.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`withdrawal-period-${item.id}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === 'custom_month' && (
            <div className="flex max-w-full flex-wrap items-center gap-2" role="group" aria-label="Custom month">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                Month
                <select
                  value={selectedMonth}
                  onChange={event => setCustomMonth(`${selectedYear}-${event.target.value}`)}
                  className="min-h-10 rounded-lg border border-white/10 bg-[#0B0E14] px-2 text-base text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D4AF37]"
                  data-testid="withdrawal-custom-month"
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={name} value={String(index + 1).padStart(2, '0')}>{name}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                Year
                <select
                  value={selectedYear}
                  onChange={event => setCustomMonth(`${event.target.value}-${selectedMonth}`)}
                  className="min-h-10 rounded-lg border border-white/10 bg-[#0B0E14] px-2 text-base text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D4AF37]"
                  data-testid="withdrawal-custom-year"
                >
                  {monthYears.map(year => <option key={year} value={year}>{year}</option>)}
                </select>
              </label>
            </div>
          )}
          <button
            type="button"
            onClick={refresh}
            className="rounded-lg border border-white/10 bg-[#0B0E14] p-2 text-gray-400 transition hover:text-white"
            title="Refresh"
            data-testid="withdrawal-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading || requestsLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>}
      </div>

      {readOnly && <p className="text-sm text-gray-300" data-testid="withdrawal-read-only">
        {tenantRows[0]?.tenant_name || 'Your team'} · Read-only. Payment actions are not available in this portal.
      </p>}

      <div className="flex w-full max-w-full gap-2 overflow-x-auto overscroll-x-contain rounded-xl border border-white/10 bg-[#0B0E14] p-1 [-webkit-overflow-scrolling:touch] sm:w-fit">
        <button
          onClick={() => setView('dashboard')}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'dashboard' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-dashboard"
        >
          <Activity className="h-4 w-4" />
          Dashboard
        </button>
        <button
          onClick={() => setView('requests')}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'requests' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-requests"
        >
          <ArrowUpCircle className="h-4 w-4" />
          Withdrawal Requests
        </button>
        <button
          onClick={() => setView('risky')}
          className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition ${
            view === 'risky' ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid="withdrawal-tab-risky"
        >
          <AlertTriangle className="h-4 w-4" />
          Risky Withdrawals
        </button>
        {disputesAllowed && <button
          type="button"
          onClick={() => setView('disputes')}
          aria-pressed={showingDisputes}
          className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37] ${
            showingDisputes ? 'bg-[#D4AF37] text-black' : 'text-gray-300 hover:text-white'
          }`}
          data-testid="withdrawal-tab-disputes"
        >
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          Disputes
        </button>}
      </div>

      {!showingDisputes && (error || requestError) && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="withdrawal-portal-error">
          {requestError || error}
        </div>
      )}

      {showingDisputes ? (
        <Suspense fallback={<p role="status" className="py-6 text-sm text-gray-300">Loading disputes…</p>}>
          <DisputesPage embedded />
        </Suspense>
      ) : view === 'dashboard' ? (
        <DashboardView
          loading={loading}
          totals={totals}
          tenants={tenantRows}
          onConfigureExpress={!readOnly && basePath.includes('/api/admin/') ? openExpressConfig : null}
        />
      ) : (
        <RequestsView
          loading={requestsLoading}
          tenants={tenants}
          tenantId={tenantId}
          setTenantId={setTenantId}
          status={status}
          setStatus={setStatus}
          cashoutMode={cashoutMode}
          setCashoutMode={setCashoutMode}
          riskIndex={riskIndex}
          setRiskIndex={setRiskIndex}
          search={search}
          setSearch={setSearch}
          requests={requests}
          result={requestResult}
          offset={offset}
          setOffset={(nextOffset) => setRequestPage({
            filterKey, offset: nextOffset, window: requestResult?.window,
          })}
          openDetail={openDetail}
          mode={view}
          tenantScoped={readOnly}
        />
      )}

      <Modal
        open={detailLoading || !!detail}
        onClose={() => { if (!detailLoading) setDetail(null); }}
        title="Withdrawal Details"
        maxWidth="max-w-4xl"
      >
        {detailLoading ? (
          <div className="flex h-64 items-center justify-center"><div className="spinner" /></div>
        ) : detail ? (
          <DetailView detail={detail} onAction={readOnly ? null : applyDetailAction} actionBusy={actionBusy} allowExpressProceed={!readOnly && canProceedExpressFromPortal(basePath)} />
        ) : null}
      </Modal>

      <Modal
        open={!!expressTenant}
        onClose={() => { if (!expressBusy) { setExpressTenant(null); setExpressConfig(null); setExpressDryRunResult(null); } }}
        title={`Express Payout · ${expressTenant?.tenant_name || ''}`}
        maxWidth="max-w-xl"
      >
        {expressBusy && !expressConfig ? (
          <div className="flex h-48 items-center justify-center"><div className="spinner" /></div>
        ) : expressConfig ? (
          <ExpressPayoutConfigForm config={expressConfig} busy={expressBusy} onSave={saveExpressConfig} onDryRun={runExpressDryRun} dryRunResult={expressDryRunResult} />
        ) : null}
      </Modal>
    </div>
  );
}

function DashboardView({ loading, totals, tenants, onConfigureExpress }) {
  if (loading) return <div className="flex h-64 items-center justify-center"><div className="spinner" /></div>;
  return (
    <div className="space-y-4" data-testid="withdrawal-dashboard">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Metric icon={ArrowUpCircle} label="Total Cashouts" value={int(totals.total_count)} sub={money(totals.total_amount)} color="#fb7185" />
        <Metric icon={Activity} label="Active" value={int(totals.active_count)} sub={money(totals.active_amount)} color="#facc15" />
        <Metric icon={Clock} label="Pending" value={int(totals.pending_count)} sub={money(totals.pending_amount)} color="#fbbf24" />
        <Metric icon={ArrowDownCircle} label="Approved" value={int(totals.approved_count)} sub={money(totals.approved_amount)} color="#60a5fa" />
        <Metric icon={Wallet} label="Paid" value={int(totals.paid_count)} sub={money(totals.paid_amount)} color="#22c55e" />
      </div>

      <div className="glass-card overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          <Building2 className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="text-sm font-semibold text-white">Tenant Cashout Totals</h3>
          <span className="text-[10px] text-gray-500">{tenants.length} tenants</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="withdrawal-tenant-table">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Active</th>
                <th className="px-4 py-3 text-right">Pending</th>
                <th className="px-4 py-3 text-right">Approved</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Amount</th>
                {onConfigureExpress && <th className="px-4 py-3 text-right">Express</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {tenants.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">No tenant cashouts in this period.</td></tr>
              ) : tenants.map((tenant) => (
                <tr key={tenant.tenant_id} className="hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <TenantLogo tenant={tenant} />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{tenant.tenant_name}</p>
                        <p className="text-[11px] text-gray-500">/{tenant.tenant_slug || '-'} · {tenant.source_vps || 'local'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-white">{int(tenant.total_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-yellow-300">{int(tenant.active_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-yellow-400">{int(tenant.pending_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-300">{int(tenant.approved_count)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-300">{int(tenant.paid_count)}</td>
                  <td className="px-4 py-3 text-right font-black tabular-nums text-red-300">{money(tenant.total_amount)}</td>
                  {onConfigureExpress && (
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => onConfigureExpress(tenant)} className="rounded-lg border border-violet-400/30 bg-violet-500/10 px-3 py-1.5 text-xs font-bold text-violet-200 hover:bg-violet-500/20">
                        Configure
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ExpressPayoutConfigForm({ config, busy, onSave, onDryRun, dryRunResult }) {
  const [form, setForm] = useState({
    mode: config.mode || 'off',
    enabled: Boolean(config.enabled),
    customer_enabled: Boolean(config.customer_enabled),
    group_id: config.group_id || '',
    group_name: config.group_name || '',
    payment_path: config.payment_path || 'website',
  });
  useEffect(() => setForm({
    mode: config.mode || 'off',
    enabled: Boolean(config.enabled),
    customer_enabled: Boolean(config.customer_enabled),
    group_id: config.group_id || '',
    group_name: config.group_name || '',
    payment_path: config.payment_path || 'website',
  }), [config]);
  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const readiness = [
    ['API key', config.api_key_configured],
    ['Callback secret', config.callback_secret_configured],
    ['Data key', config.data_key_configured],
    ['Selected mode server gate', config.server_gate],
  ];
  return (
    <div className="space-y-4" data-testid="express-payout-config">
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3 text-xs text-amber-100">
        Live stays locked until the live server gate is enabled. Enable one team at a time only after its dry-run passes.
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-gray-400">Mode
          <select value={form.mode} onChange={(e) => update('mode', e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-white">
            <option value="off">Off</option><option value="dry_run">Dry run</option><option value="live">Live</option>
          </select>
        </label>
        <label className="text-xs text-gray-400">Payment path
          <select value={form.payment_path} onChange={(e) => update('payment_path', e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-white">
            <option value="website">Website</option><option value="manual">Manual</option>
          </select>
        </label>
        <label className="text-xs text-gray-400">Immutable groupId
          <input value={form.group_id} onChange={(e) => update('group_id', e.target.value.toLowerCase())} className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-white" placeholder="mystical" />
        </label>
        <label className="text-xs text-gray-400">Display groupName
          <input value={form.group_name} onChange={(e) => update('group_name', e.target.value)} className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-white" placeholder="Mystical" />
        </label>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex items-center gap-2 rounded-lg border border-white/10 p-3 text-sm text-gray-200"><input type="checkbox" checked={form.enabled} onChange={(e) => update('enabled', e.target.checked)} /> Provider integration enabled</label>
        <label className="flex items-center gap-2 rounded-lg border border-white/10 p-3 text-sm text-gray-200"><input type="checkbox" checked={form.customer_enabled} onChange={(e) => update('customer_enabled', e.target.checked)} /> Show Express to customers</label>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {readiness.map(([label, ready]) => <div key={label} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-gray-300">{ready ? '✓' : '✕'} {label}</div>)}
      </div>
      <p className="text-[11px] text-gray-500">Callback URL: {config.callback_url}</p>
      {form.mode === 'dry_run' && (
        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.05] p-3">
          <button type="button" disabled={busy || !config.server_gate || !config.enabled} onClick={() => onDryRun()} className="h-10 w-full rounded-lg bg-cyan-500 text-sm font-black text-slate-950 disabled:opacity-40">
            Run isolated $0.01 dry-run
          </button>
          {dryRunResult?.payout_id && (
            <div className="mt-3 space-y-2 text-xs text-cyan-100">
              <p>✓ Create HTTP {dryRunResult.create_http} · replay HTTP {dryRunResult.replay_http} · wallet mutation: none</p>
              <p className="break-all text-cyan-200/70">{dryRunResult.payout_id}</p>
              <div className="flex gap-2">
                <button type="button" disabled={busy} onClick={() => onDryRun({ payout_id: dryRunResult.payout_id, state: 2 })} className="rounded-lg border border-emerald-400/30 px-3 py-2 font-bold text-emerald-200">Simulate Done</button>
                <button type="button" disabled={busy} onClick={() => onDryRun({ payout_id: dryRunResult.payout_id, state: 3 })} className="rounded-lg border border-red-400/30 px-3 py-2 font-bold text-red-200">Simulate Failed</button>
              </div>
            </div>
          )}
        </div>
      )}
      <button type="button" disabled={busy} onClick={() => onSave(form)} className="h-11 w-full rounded-xl bg-violet-600 text-sm font-black text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save fail-closed configuration'}</button>
    </div>
  );
}

function RequestsView({
  loading,
  tenants,
  tenantId,
  setTenantId,
  status,
  setStatus,
  cashoutMode,
  setCashoutMode,
  riskIndex,
  setRiskIndex,
  search,
  setSearch,
  requests,
  result,
  offset,
  setOffset,
  openDetail,
  mode,
  tenantScoped = false,
}) {
  const isRisky = mode === 'risky';
  const total = Number(result?.total || 0);
  const pageSize = Number(result?.limit || 300);
  const incomplete = result?.complete === false;
  return (
    <div className="space-y-4" data-testid="withdrawal-requests-portal">
      <div className={`grid gap-3 md:grid-cols-2 ${tenantScoped ? 'xl:grid-cols-4' : 'xl:grid-cols-[minmax(180px,250px)_minmax(140px,190px)_minmax(150px,210px)_minmax(140px,190px)_1fr]'}`}>
        {!tenantScoped && <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Tenant</span>
          <select
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-tenant-filter"
          >
            <option value="all">All teams</option>
            {tenants.map((tenant) => (
              <option key={tenant.tenant_id} value={tenant.tenant_id}>{tenant.tenant_name}</option>
            ))}
          </select>
        </label>}
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Cashout Mode</span>
          <select
            value={cashoutMode}
            onChange={(event) => setCashoutMode(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-cashout-mode-filter"
          >
            {CASHOUT_MODES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Status</span>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-status-filter"
          >
            {STATUSES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Risk Index</span>
          <select
            value={riskIndex}
            onChange={(event) => setRiskIndex(event.target.value)}
            className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
            data-testid="withdrawal-risk-index-filter"
          >
            {RISK_INDEXES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Search player</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Username, email, or name..."
              className="h-10 w-full rounded-lg border border-white/10 bg-[#0B0E14] pl-9 pr-3 text-sm text-white outline-none focus:border-[#D4AF37]/60"
              data-testid="withdrawal-player-search"
            />
          </div>
        </label>
      </div>

      <div className="glass-card overflow-hidden rounded-xl">
        <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
          {isRisky ? <AlertTriangle className="h-4 w-4 text-yellow-300" /> : <ArrowUpCircle className="h-4 w-4 text-red-300" />}
          <h3 className="text-sm font-semibold text-white">{isRisky ? 'Risky Withdrawal Watchlist' : 'Withdrawal Request Ledger'}</h3>
          <span className="text-xs text-gray-400" aria-live="polite" data-testid="withdrawal-result-count">
            {loading ? 'Loading matching requests…' : result ? (
              `${requests.length ? `${offset + 1}–${offset + requests.length}` : '0'} of ${int(total)} matching${incomplete ? ' (partial)' : ''}`
            ) : 'Results unavailable'}
          </span>
        </div>
        {incomplete && (
          <p role="alert" className="border-b border-white/5 px-4 py-3 text-sm text-yellow-200">
            Some VPS data could not be loaded. These results are incomplete. Use Refresh to retry.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="withdrawal-request-table">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Date / Time</th>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-left">Risk</th>
                <th className="px-4 py-3 text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center"><div className="spinner mx-auto" /></td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-400">{!result ? 'Results unavailable. Use Refresh to try again.' : incomplete ? 'No requests returned from the available sources. Refresh to load missing VPS data.' : isRisky ? 'No risky withdrawals match this filter.' : 'No withdrawal requests match this filter.'}</td></tr>
              ) : requests.map((row) => (
                <tr key={`${row.kind}-${row.source_vps}-${row.id}`} className="hover:bg-white/[0.03]">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-300">{time(row.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <TenantLogo tenant={row} className="h-8 w-8" textClassName="text-[10px]" />
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-white">{row.tenant_name}</p>
                        <p className="text-[10px] text-gray-500">{row.source_vps || 'local'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-white">{row.username}</p>
                    <p className="text-[10px] text-gray-500">{row.email || '-'}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-300">
                    <PaymentBrand method={row.payment_method || 'Manual'}/>
                    {row.cashout_speed === 'priority' && <p className="mt-1 text-[10px] font-bold text-[#f5db7b]">Priority · {Number(row.priority_fee_percent || 5)}% fee</p>}
                    {row.details_preview && <p className="max-w-[220px] truncate text-[10px] text-gray-500">{row.details_preview}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-black tabular-nums text-red-300">{money(row.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${statusClass(row.status_group)}`}>
                      {withdrawalStatusLabel(row)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <RiskBadge risk={row.risk} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openDetail(row)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-gray-200 transition hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
                      data-testid={`withdrawal-detail-${row.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <nav aria-label="Withdrawal ledger pages" className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 px-4 py-3">
          <p className="text-xs text-gray-400">{pageSize} per page · {incomplete ? 'Showing available sources only' : 'Browse all matching records across pages'}</p>
          <div className="flex items-center gap-2">
            <button type="button" disabled={loading || !result || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
              className="rounded-lg border border-white/10 px-3 py-2 text-sm text-white hover:border-[#D4AF37]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="withdrawal-page-previous">Previous</button>
            <button type="button" disabled={loading || !result?.has_more}
              onClick={() => setOffset(offset + pageSize)}
              className="rounded-lg border border-white/10 px-3 py-2 text-sm text-white hover:border-[#D4AF37]/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="withdrawal-page-next">Next</button>
          </div>
        </nav>
      </div>
    </div>
  );
}

function RiskBadge({ risk }) {
  const flags = risk?.flags || [];
  const severity = risk?.severity || 'clear';
  if (!flags.length) {
    return (
      <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${riskClass(severity)}`}>
        Clear
      </span>
    );
  }
  return (
    <div className="min-w-[170px] space-y-1">
      <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black uppercase ${riskClass(severity)}`}>
        {severity} · {Number(risk.score || 0)}%
      </span>
      <div className="flex max-w-[240px] flex-wrap gap-1">
        {flags.slice(0, 2).map((flag) => (
          <span key={flag.code} className="rounded-md border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[10px] text-gray-300">
            {flag.label}
          </span>
        ))}
        {flags.length > 2 && <span className="text-[10px] text-gray-500">+{flags.length - 2}</span>}
      </div>
    </div>
  );
}

function DetailView({ detail, onAction, actionBusy, allowExpressProceed }) {
  const player = detail?.player || {};
  const request = detail?.request || {};
  const risk = request.risk || {};
  const totals = player.totals || {};
  const [activeLedger, setActiveLedger] = useState('loads');
  const referralSource = player.referral_source || {};
  const referredName = referralSource.username || referralSource.full_name || player.referred_by?.username || player.referred_by?.full_name || player.referred_by_staff?.username || player.referred_by_staff?.full_name || '-';
  const referredEmail = referralSource.email || player.referred_by?.email || player.referred_by_staff?.email || '-';
  const referralCode = referralSource.source_code || referralSource.referral_code || player.referred_by?.referral_code || player.referred_by_staff?.referral_code || '-';
  const walletDeposits = player.wallet_deposits || [];
  const walletCashouts = player.wallet_cashouts || [];
  const referralRows = [
    ...(player.referral_earnings || []),
    ...(player.referred_players || []).map((row) => ({
      ...row,
      kind: 'referred_player',
      label: 'Referred player',
      method: row.email || row.full_name || '',
      reference: row.source_code || '',
      amount: null,
    })),
  ];
  const revenueRows = sortByTimeDesc([
    ...walletDeposits.map((row) => ({ ...row, label: row.label || 'Wallet topup', amount: Number(row.amount || 0), tone: 'in' })),
    ...walletCashouts.map((row) => ({ ...row, label: row.label || 'Wallet withdrawal', amount: -Math.abs(Number(row.amount || 0)), tone: 'out' })),
  ]);
  const ledgers = [
    {
      id: 'loads',
      icon: ArrowDownCircle,
      label: 'Total Load',
      value: money(totals.total_load),
      sub: `${int(totals.total_load_count)} game loads`,
      color: '#60a5fa',
      rows: player.last_load_games || [],
      empty: 'No game load activity',
    },
    {
      id: 'redeems',
      icon: ArrowUpCircle,
      label: 'Total Redeemed',
      value: money(totals.total_redeemed),
      sub: `${int(totals.total_redeem_count)} game redeems`,
      color: '#22c55e',
      rows: player.last_withdrawal_from_games || [],
      empty: 'No game redeem activity',
    },
    {
      id: 'wallet-deposits',
      icon: Wallet,
      label: 'Wallet Topups',
      value: money(totals.wallet_topups ?? totals.wallet_deposited),
      sub: `${int(totals.wallet_deposit_count)} topups`,
      color: '#D4AF37',
      rows: walletDeposits,
      empty: 'No wallet topups',
    },
    {
      id: 'wallet-cashouts',
      icon: ArrowUpCircle,
      label: 'Wallet Withdrawals',
      value: money(totals.wallet_cashout),
      sub: `${int(totals.wallet_cashout_count)} withdrawals`,
      color: '#fb7185',
      rows: walletCashouts,
      empty: 'No wallet withdrawals',
    },
    {
      id: 'revenue',
      icon: Activity,
      label: 'Revenue',
      value: money(totals.revenue),
      sub: 'topups - withdrawals',
      color: Number(totals.revenue || 0) >= 0 ? '#22c55e' : '#fb7185',
      rows: revenueRows,
      empty: 'No revenue activity',
    },
    {
      id: 'referrals',
      icon: Users,
      label: 'Referral Earned',
      value: money(totals.total_earned_by_referral),
      sub: `${int(totals.total_referrals)} referrals`,
      color: '#a78bfa',
      rows: referralRows,
      empty: 'No referral activity',
    },
  ];
  const selectedLedger = ledgers.find((item) => item.id === activeLedger) || ledgers[0];

  return (
    <div className="space-y-4" data-testid="withdrawal-detail-modal">
      <div className="grid gap-3 md:grid-cols-3">
        <Info label="Player" value={player.username || request.username || '-'} sub={player.email || request.email || ''} icon={UserRound} />
        <Info label="Tenant" value={player.tenant_name || request.tenant_name || '-'} sub={player.tenant_slug ? `/${player.tenant_slug}` : ''} icon={Building2} />
        <Info label="Request" value={money(request.amount)} sub={`${request.status || '-'} · ${time(request.created_at)}`} icon={ArrowUpCircle} />
      </div>

      <RiskReview risk={risk} />

      <WithdrawalActions request={request} onAction={onAction} busy={actionBusy} allowExpressProceed={allowExpressProceed} />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {ledgers.map((item) => (
          <Metric
            key={item.id}
            icon={item.icon}
            label={item.label}
            value={item.value}
            sub={item.sub}
            color={item.color}
            active={activeLedger === item.id}
            onClick={() => setActiveLedger(item.id)}
          />
        ))}
      </div>

      <DetailLedger title={selectedLedger.label} rows={selectedLedger.rows} empty={selectedLedger.empty} signed={selectedLedger.id === 'revenue'} />

      <div className="grid gap-3 lg:grid-cols-2">
        <Timeline title="Last Load Games" rows={player.last_load_games || []} empty="No recent game loads" />
        <Timeline title="Last Withdrawal From Games" rows={player.last_withdrawal_from_games || []} empty="No recent game redeems" />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Player Profile</p>
          <dl className="mt-3 space-y-2 text-sm">
            <Line label="Join date" value={time(player.join_date)} />
            <Line label="Phone" value={player.phone || '-'} />
            <Line label="Last load amounts" value={(player.last_load_amounts || []).length ? player.last_load_amounts.map(money).join(', ') : '-'} />
          </dl>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Referral Source</p>
          <dl className="mt-3 space-y-2 text-sm">
            <Line label="Source" value={referralSource.label || '-'} />
            <Line label="Referred name" value={referredName} />
            <Line label="Referred email" value={referredEmail} />
            <Line label="Referral code" value={referralCode} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function WithdrawalActions({ request, onAction, busy, allowExpressProceed = false }) {
  const [amount, setAmount] = useState(String(request?.amount || ''));
  const [reason, setReason] = useState('');
  const status = String(request?.status || '').toLowerCase();
  const isManualWithdrawal = request?.kind === 'payment_request' && ['withdrawal', 'withdraw'].includes(String(request?.payout_type || '').toLowerCase());
  const isExpress = ['express', 'priority'].includes(String(request?.cashout_speed || '').toLowerCase());
  const isPriority = String(request?.cashout_speed || '').toLowerCase() === 'priority';

  useEffect(() => setAmount(String(request?.amount || '')), [request?.id, request?.amount]);
  useEffect(() => setReason(''), [request?.id, status]);

  if (!isManualWithdrawal || !onAction) return null;
  if (isPriority && request?.priority_payout?.dispatch_ready !== true) return (
    <section className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-4" data-testid="priority-payout-controls">
      <p className="text-sm font-bold text-[#f5db7b]">Priority payout controls</p>
      <p className="mt-2 text-sm text-gray-300">Payout {money(request.net_payout)} · Priority fee {money(request.priority_fee_amount)} ({Number(request.priority_fee_percent || 5)}%).</p>
      <p className="mt-2 text-xs text-gray-400">Separate 1Click integration is not connected. No payout has been dispatched.</p>
      <button type="button" disabled className="mt-4 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-black disabled:cursor-not-allowed disabled:opacity-50">Proceed Priority payout — setup required</button>
    </section>
  );
  const needReason = (action) => {
    if (!reason.trim()) return false;
    onAction(action, { reason: reason.trim() });
    return true;
  };
  return (
    <section className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-4" data-testid="withdrawal-correction-actions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-[#f5db7b]">{isPriority ? 'Priority payout controls' : isExpress ? 'Express payout controls' : 'Finance correction controls'}</p>
          <p className="mt-1 text-xs text-gray-400">
            {isExpress
              ? `Provider receives ${money(request.net_payout)} after a ${money(isPriority ? request.priority_fee_amount : request.express_fee_amount)} ${isPriority ? 'Priority' : 'Express'} fee. Proceed is final once accepted.`
              : 'Every action is audited. A return to pending never refunds the player by itself.'}
          </p>
        </div>
        <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold uppercase text-gray-300">{status || 'unknown'}</span>
      </div>

      {!isExpress && status === 'approved' && (
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto]">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Correction reason (required)" className="h-10 rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60" />
          <button type="button" disabled={busy || !reason.trim()} onClick={() => needReason('return_pending')} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-black disabled:cursor-not-allowed disabled:opacity-50">
            {busy ? 'Saving…' : 'Return to pending (24h)'}
          </button>
        </div>
      )}

      {status === 'pending' && (
        <div className="mt-3 space-y-3">
          <div className="grid gap-2 md:grid-cols-[minmax(150px,240px)_auto_1fr]">
            <input type="number" min="0.01" max={request.correction_original_amount ? Number(request.amount) : undefined} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60" aria-label="Corrected withdrawal amount" />
            <button type="button" disabled={busy || !Number(amount) || Number(amount) <= 0} onClick={() => onAction('edit_amount', { amount: Number(amount) })} className="rounded-lg border border-blue-400/40 bg-blue-500/15 px-4 py-2 text-sm font-bold text-blue-200 disabled:opacity-50">
              Save actual paid amount
            </button>
            <p className="self-center text-xs text-gray-400">Reducing this amount immediately returns only the difference to Main Wallet.</p>
          </div>
          <div className={`grid gap-2 ${isExpress ? 'md:grid-cols-[1fr_auto_auto]' : 'md:grid-cols-[1fr_auto_auto]'}`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Cancellation reason (required for refund)" className="h-10 rounded-lg border border-white/10 bg-[#0B0E14] px-3 text-sm text-white outline-none focus:border-[#D4AF37]/60" />
            <button type="button" disabled={busy || !reason.trim()} onClick={() => needReason('reject')} className="rounded-lg border border-red-400/40 bg-red-500/15 px-4 py-2 text-sm font-bold text-red-200 disabled:opacity-50">Cancel & refund</button>
            <button type="button" disabled={busy || (isExpress && !allowExpressProceed)} onClick={() => onAction(isExpress ? 'proceed' : 'approve')} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-black disabled:opacity-50">
              {busy ? 'Submitting…' : isExpress ? (allowExpressProceed ? 'Proceed payout' : 'Finance or Master required') : 'Approve final amount'}
            </button>
          </div>
        </div>
      )}

      {isExpress && ['processing', 'done', 'failed'].includes(status) && (
        <>
          <div className="mt-3 rounded-lg border border-white/10 bg-[#0B0E14] px-3 py-2 text-xs text-gray-300">
            Provider status: <strong>{request?.express_payout?.status || status}</strong>
            {request?.express_payout?.submission_uncertain && ' · Submission uncertain — reconciliation only; no automatic retry or refund.'}
          </div>
          {(request?.failure_reason || request?.express_payout?.error) && (
            <div className="mt-2 rounded-lg border border-red-400/25 bg-red-950/25 px-3 py-2 text-xs text-red-200">
              Failure reason: <strong>{request?.failure_reason || request?.express_payout?.error}</strong>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function RiskReview({ risk }) {
  const flags = risk?.flags || [];
  const metrics = risk?.metrics || {};
  if (!flags.length) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-gray-500" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">Risk Review</p>
          <span className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${riskClass('clear')}`}>Clear</span>
        </div>
        <p className="mt-2 text-xs text-gray-500">No suspicious withdrawal signals found for this request.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/[0.06] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-300" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-yellow-100/70">Risk Review</p>
        <span className={`rounded-full border px-2 py-1 text-[10px] font-black uppercase ${riskClass(risk.severity)}`}>
          {risk.severity || 'risk'} · {Number(risk.score || 0)}%
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {flags.map((flag) => (
          <span key={flag.code} className="rounded-lg border border-white/10 bg-[#0B0E14]/70 px-2 py-1 text-xs text-gray-200">
            {flag.label}
          </span>
        ))}
      </div>
      <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Line label="Wallet topups" value={money(metrics.wallet_topups)} />
        <Line label="Cashout exposure" value={money(metrics.cashout_exposure)} />
        <Line label="Projected revenue" value={signedMoney(metrics.projected_revenue)} />
        <Line label="Game activity" value={`${money(metrics.total_game_load)} / ${money(metrics.total_game_redeemed)}`} />
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, color, onClick, active = false }) {
  const className = `rounded-xl border p-4 text-left transition ${
    active
      ? 'border-[#D4AF37]/60 bg-[#1A2030] shadow-[0_0_0_1px_rgba(212,175,55,0.16)]'
      : 'border-white/10 bg-[#131822]'
  } ${onClick ? 'cursor-pointer hover:border-[#D4AF37]/45 hover:bg-[#161C28]' : ''}`;
  const content = (
    <>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="mt-1 text-[11px] text-gray-500">{sub}</p>
    </>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }
  return (
    <div className={className}>
      {content}
    </div>
  );
}

function Info({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#D4AF37]" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{label}</p>
      </div>
      <p className="truncate text-lg font-bold text-white">{value}</p>
      {sub && <p className="truncate text-[11px] text-gray-500">{sub}</p>}
    </div>
  );
}

function Timeline({ title, rows, empty }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0E14] p-4">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{title}</p>
      {rows.length === 0 ? (
        <p className="py-5 text-center text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div key={`${row.id || index}-${row.created_at}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{row.game || row.provider || 'Unknown game'}</p>
                <p className="text-[10px] text-gray-500">{row.provider || '-'} · {time(row.created_at)}</p>
              </div>
              <p className="shrink-0 font-black tabular-nums text-[#D4AF37]">{money(row.amount)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailLedger({ title, rows, empty, signed = false }) {
  const sortedRows = sortByTimeDesc(rows || []);
  return (
    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#0B0E14] p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{title} Details</p>
        <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] font-semibold text-gray-400">{sortedRows.length} rows</span>
      </div>
      {sortedRows.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-500">{empty}</p>
      ) : (
        <div className="max-h-[330px] overflow-auto rounded-lg border border-white/5">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#101620] text-[10px] uppercase tracking-[0.14em] text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Date / Time</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Detail</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sortedRows.map((row, index) => {
                const hasAmount = row.amount !== null && row.amount !== undefined && row.amount !== '';
                const amount = Number(row.amount || 0);
                const source = row.game || row.label || row.provider || row.username || row.method || 'Activity';
                const detailBits = [
                  row.provider && row.provider !== source ? row.provider : null,
                  row.method && row.method !== source ? row.method : null,
                  row.status,
                  row.reference,
                ].filter(Boolean);
                return (
                  <tr key={`${row.id || row.reference || index}-${row.created_at || index}`} className="hover:bg-white/[0.03]">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-300">{time(row.created_at)}</td>
                    <td className="max-w-[220px] px-3 py-2">
                      <p className="truncate font-semibold text-white">{source}</p>
                    </td>
                    <td className="max-w-[260px] px-3 py-2 text-xs text-gray-500">
                      <p className="truncate">{detailBits.length ? detailBits.join(' · ') : '-'}</p>
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 text-right font-black tabular-nums ${
                      signed && amount < 0 ? 'text-red-300' : 'text-[#D4AF37]'
                    }`}>
                      {hasAmount ? (signed ? signedMoney(amount) : money(amount)) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="truncate text-right font-semibold text-gray-100">{value}</dd>
    </div>
  );
}
